import { Avatar, IconButton, List, ListItem } from "@mui/material";
import { Box, styled } from "@mui/system";
import { FC, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumb from "../../../components/breadcrumb/Breadcrumb";
import { GullButton } from "../../../components/buttons/GullButton";
import { CardContainer } from "../../../components/CardContainer";
import InboxNavDrawer from "../../../components/drawer/InboxNavDrawer";
import muiIcons from "../../../components/icons/mui-icons";
import ComposeModal from "../../../components/modals/ComposeModal";
import { H3, Paragraph, Span } from "../../../components/Typography";
import useCommonContext from "../../../hooks/useCommonContext";
import { themeShadows } from "../../../theme/themeColors";
import { inboxMenuData, messageList } from "./inbox-data";

const InboxMenu = styled(Box)(({ theme }) => ({
  width: "15%",
  paddingLeft: "10px",
  paddingRight: "20px",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const MenuItem = styled(ListItem)(({ theme }) => ({
  padding: "10px 2px",
  "& a": {
    display: "flex",
    alignItems: "center",
    "& svg": {
      fontSize: 20,
      marginRight: "8px",
    },
  },
  "& :hover": {
    color: theme.palette.primary.main,
  },
}));

const MessageContainer = styled(CardContainer)(({ theme }) => ({
  padding: 0,
  width: "85%",
  height: "600px",
  display: "flex",
  [theme.breakpoints.down("md")]: {
    width: "100%",
  },
}));

const MessageList = styled(Box)(({ theme }) => ({
  width: "30%",
  height: "100%",
  cursor: "pointer",
  overflowY: "auto",
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  "& :hover": {
    background: theme.palette.mode === "light" ? theme.palette.grey[50] : "#0000002e",
  },
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const Message = styled(Box)(({ theme }) => ({
  display: "flex",
  padding: "20px 15px",
  borderBottom: `1px solid ${theme.palette.grey[300]}`,
  "& .userName": {
    width: "100%",
    display: "flex",
    fontSize: 12,
    color: theme.palette.grey[600],
    justifyContent: "space-between",
  },
  "& :hover": {
    background: "transparent",
  },
}));

const MessageBox = styled(Box)(({ theme }) => ({
  width: "70%",
  overflowY: "auto",
  transition: "all .4s",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    transition: "all .4s",
  },
}));

const MessageHeader = styled(Box)(({ theme }) => ({
  height: "50px",
  display: "flex",
  padding: "0px 10px",
  alignItems: "center",
  zIndex: 1500,
  boxShadow: themeShadows[1],
}));

const HeadItem = styled(Box)(({ theme }) => ({
  padding: "10px 12px",
  "& a": {
    display: "flex",
    alignItems: "center",
    "& svg": {
      fontSize: 20,
      marginRight: "8px",
    },
  },
  "& :hover": {
    color: theme.palette.primary.main,
  },
}));

const StyledIconButton = styled(IconButton)(({ theme }) => ({
  marginRight: "10px",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const MessageBody = styled(Box)(({ theme }) => ({
  padding: "1.5rem",
  "& p": {
    fontSize: "13px",
  },
}));

const Inbox: FC = () => {
  const { EmailIcon, MenuIcon } = muiIcons;
  const { setComposeFormModal } = useCommonContext();
  const [chatListDrawer, setChatListDrawer] = useState<boolean>(false);

  return (
    <>
      <Breadcrumb name="Apps" subName="Inbox" path="/dashboard/v1" />

      <Box display="flex" position="relative">
        <InboxMenu>
          <GullButton
            fullWidth
            rounded={true}
            variant="contained"
            color="primary"
            sx={{ m: 0 }}
            onClick={() => setComposeFormModal(true)}
          >
            Compose
          </GullButton>
          <Paragraph sx={{ pl: "2px", mt: "20px" }}>Browse</Paragraph>
          <List>
            {inboxMenuData.map((data) => (
              <MenuItem>
                <Link to="#">
                  <data.Icon /> {data.name}
                </Link>
              </MenuItem>
            ))}
          </List>
        </InboxMenu>
        <ComposeModal />
        <MessageContainer sx={{ position: "relative" }}>
          <MessageList>
            {messageList.map((data) => {
              const { name, date, message, photoUrl } = data;
              return (
                <Message>
                  <Avatar
                    src={photoUrl}
                    sx={{ mr: "10px", width: 35, height: 35 }}
                  />
                  <Box width="100%">
                    <Paragraph className="userName">
                      <Span>{name}</Span>
                      <Span>{date}</Span>
                    </Paragraph>
                    <Paragraph sx={{ fontSize: 13 }}>{message}</Paragraph>
                  </Box>
                </Message>
              );
            })}
          </MessageList>

          <MessageBox sx={{ position: "relative" }}>
            <InboxNavDrawer
              messageList={messageList}
              inboxMenuData={inboxMenuData}
              chatListDrawer={chatListDrawer}
              setChatListDrawer={setChatListDrawer}
            />
            <MessageHeader>
              <StyledIconButton
                onClick={() =>
                  chatListDrawer
                    ? setChatListDrawer(false)
                    : setChatListDrawer(true)
                }
              >
                <MenuIcon />
              </StyledIconButton>
              <HeadItem>
                <Link to="#">
                  <EmailIcon /> Replay
                </Link>
              </HeadItem>
              <HeadItem>
                <Link to="#">
                  <EmailIcon /> Forward
                </Link>
              </HeadItem>
              <HeadItem>
                <Link to="#">
                  <EmailIcon /> Delete
                </Link>
              </HeadItem>
            </MessageHeader>

            <MessageBody>
              <Box display="flex">
                <Avatar
                  src="/assets/images/faces/1.jpg"
                  sx={{ mr: "10px", width: 35, height: 35 }}
                />
                <Box>
                  <Paragraph>Jhon Doe</Paragraph>
                  <Paragraph>20 Oct 2021</Paragraph>
                </Box>
              </Box>
              <H3 pt="15px">Confirm your email</H3>
              <Paragraph padding="10px 0px">
                Natus consequuntur perspiciatis esse beatae illo quos eaque.
              </Paragraph>
              <Paragraph padding="10px 0px">
                Earum, quisquam, fugit? Numquam dolor magni nisi? Suscipit odit,
                ipsam iusto enim culpa, temporibus vero possimus error
                voluptates sequi. Iusto ipsam, nihil? Eveniet modi maxime animi
                excepturi a dignissimos doloribus, inventore sed ratione,
                ducimus atque earum maiores tenetur officia commodi dicta
                tempora consequatur non nesciunt ipsam, consequuntur quia fuga
                aspernatur impedit et? Natus, earum.
              </Paragraph>

              <H3 padding="5px 0px">
                Earum, quisquam, fugit? Numquam dolor magni nisi? Suscipit odit,
                ipsam iusto enim culpa, temporibus vero possimus error
                voluptates sequi.
              </H3>
              <Paragraph padding="10px 0px">
                Earum, quisquam, fugit? Numquam dolor magni nisi? Suscipit odit,
                ipsam iusto enim culpa, temporibus vero possimus error
                voluptates sequi. Iusto ipsam, nihil? Eveniet modi maxime animi
                excepturi a dignissimos doloribus, inventore sed ratione,
                ducimus atque earum maiores tenetur officia commodi dicta
                tempora consequatur non nesciunt ipsam, consequuntur quia fuga
                aspernatur impedit et? Natus, earum.
              </Paragraph>
              <Box mt="15px">
                <Paragraph>Thank you</Paragraph>
                <Paragraph>Jhon</Paragraph>
              </Box>
            </MessageBody>
          </MessageBox>
        </MessageContainer>
      </Box>
    </>
  );
};

export default Inbox;
